/**
 * LoanTenureDropdownInput
 *
 * Generic dropdown component for loan tenure selection.
 * Sends numeric values (in days) to orchestrator for data saving.
 * Backend converts to months/weeks based on repayment frequency.
 *
 * Features:
 * - Dropdown with predefined tenure options (90, 180, 360 days)
 * - Displays user-friendly labels
 * - Mobile-responsive
 * - Orchestrator-compatible
 */

import React, { useState, useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { FileText } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import KFSModal from '@/components/LOS/Caprion/KFSModal';

interface LoanTenureDropdownInputProps {
  // Controlled pattern (used by JourneyPage)
  value?: { loan_tenure?: number | null };
  onChange?: (val: { loan_tenure: number }) => void;
  disabled?: boolean;
  // Legacy pattern
  initialData?: { loan_tenure?: number };
  onDataChange?: (data: { loan_tenure: number }) => void;
}

const LoanTenureDropdownInput: React.FC<LoanTenureDropdownInputProps> = ({
  value,
  onChange,
  disabled = false,
  initialData,
  onDataChange
}) => {
  // Tenure options in days (90, 180, 360)
  const TENURE_OPTIONS = [
    { value: 90, label: '90 days' },
    { value: 180, label: '180 days' },
    { value: 360, label: '360 days' }
  ];

  const DEFAULT_TENURE = 180;

  // Current value
  const currentValue = value?.loan_tenure || initialData?.loan_tenure || DEFAULT_TENURE;

  // Handle change
  const handleChange = useCallback((val: string) => {
    const tenureValue = parseInt(val, 10);
    
    // Call both callbacks for compatibility
    onChange?.({ loan_tenure: tenureValue });
    onDataChange?.({ loan_tenure: tenureValue });
  }, [onChange, onDataChange]);

  const [isKFSModalOpen, setIsKFSModalOpen] = useState(false);

  return (
    <div className="space-y-1.5">
      <Label htmlFor="loan_tenure" className="text-sm font-medium">
        Loan Tenure <span className="text-red-500">*</span>
      </Label>
      
      <Select
        value={currentValue?.toString()}
        onValueChange={handleChange}
        disabled={disabled}
      >
        <SelectTrigger id="loan_tenure" className="w-full text-intellend-green font-medium">
          <SelectValue placeholder="Select tenure" />
        </SelectTrigger>
        <SelectContent>
          {TENURE_OPTIONS.map(option => (
            <SelectItem key={option.value} value={option.value.toString()}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Helper text */}
      <p className="text-xs text-gray-400">
        Select the repayment period for your loan
      </p>

      {/* View KFS Link */}
      <div className="flex justify-center pt-2">
        <button
          onClick={() => setIsKFSModalOpen(true)}
          className="text-sm text-intellend-green hover:underline flex items-center gap-1.5 font-medium"
        >
          <FileText className="w-4 h-4" />
          View Key Fact Statement (KFS)
        </button>
      </div>

      {/* KFS Modal */}
      <KFSModal
        isOpen={isKFSModalOpen}
        onClose={() => setIsKFSModalOpen(false)}
      />
    </div>
  );
};

export default LoanTenureDropdownInput;
